import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { requestTopic } from '../../services/credits';

// Modal that lets a user request a new topic for a company. The submission
// goes into the `topics` table with status='pending'; an admin promotes it
// from the existing admin-panel flow.

function RequestTopicDialog({ open, companyId, onClose, onCreated }) {
    const [topic, setTopic] = useState('');
    const [relationship, setRelationship] = useState('product');
    const [additionalText, setAdditionalText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const reset = () => {
        setTopic(''); setRelationship('product'); setAdditionalText(''); setError(null);
    };

    const submit = async () => {
        setError(null);
        if (!topic.trim()) { setError('Topic name is required.'); return; }
        setSubmitting(true);
        try {
            const result = await requestTopic({
                companyId,
                topic: topic.trim(),
                relationship,
                additional_text: additionalText.trim(),
            });
            reset();
            onCreated(result);
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Request a topic</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Topics are reviewed before becoming available. Your request will be
                    attached to this project as <strong>pending</strong>.
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    label="Topic name"
                    value={topic}
                    onChange={e => setTopic(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Relationship</InputLabel>
                    <Select
                        value={relationship}
                        label="Relationship"
                        onChange={e => setRelationship(e.target.value)}
                    >
                        <MenuItem value="product">Product</MenuItem>
                        <MenuItem value="competitor">Competitor</MenuItem>
                        <MenuItem value="general">General</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    fullWidth
                    label="Additional context (optional)"
                    value={additionalText}
                    onChange={e => setAdditionalText(e.target.value)}
                    multiline
                    rows={2}
                />
                {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { reset(); onClose(); }} disabled={submitting}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Request'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default RequestTopicDialog;
